// Graph sharing functionality
import {
  buildIpynbNotebook,
  fetchServerIpynb,
  fetchServerDashboardIpynb,
  buildMarimoNotebook,
  downloadNotebookText,
  notebookFilename
} from './notebook-export.js';

// Create graph sharing functionality
function createGraphSharing() {
  const API_SHARE_ENDPOINT = '/api/share-graph';
  const API_GET_SHARED_ENDPOINT = '/api/get-shared-graph';
  const API_RESOLVE_SHARE_TOKEN_ENDPOINT = '/api/resolve-share-token';
  const API_REGISTER_SHARE_TOKEN_ENDPOINT = '/api/register-share-token';

  const sharingSelf = { lastRedirectAttempt: null };

  const MAX_LIST_ITEMS = 25;
  const MAX_METADATA_ENTRIES = 20;
  const MAX_METADATA_VALUE_LENGTH = 256;

  let baseShareOptions = {
    isPublic: true,
    sharedWithEmails: [],
    plugins: [],
    dataPlatforms: [],
    exportTargets: [],
    metadata: {},
    query: null
  };

  function isPlainObject(value) {
    return (
      value !== null &&
      typeof value === 'object' &&
      Object.prototype.toString.call(value) === '[object Object]'
    );
  }

  function coerceStringList(value, fallback = [], validator) {
    const initial = (() => {
      if (!value && value !== 0) return Array.isArray(fallback) ? [...fallback] : [];
      if (Array.isArray(value)) return value;
      if (typeof value === 'string') {
        if (!value.trim()) return [];
        return value.split(',').map(item => item.trim());
      }
      return Array.isArray(fallback) ? [...fallback] : [];
    })();

    const filtered = initial
      .map(item => (typeof item === 'string' ? item.trim() : ''))
      .filter(item => item.length > 0 && (!validator || validator(item)));

    const unique = Array.from(new Set(filtered));
    if (unique.length > MAX_LIST_ITEMS) {
      return unique.slice(0, MAX_LIST_ITEMS);
    }
    return unique;
  }

  function mergeUniqueLists(primary = [], secondary = []) {
    const merged = [];
    const seen = new Set();
    const pushValue = value => {
      if (value === undefined || value === null) return;
      let candidate = value;
      let key;

      if (typeof candidate === 'string') {
        const trimmed = candidate.trim();
        if (!trimmed) return;
        candidate = trimmed;
        key = `str:${trimmed.toLowerCase()}`;
      } else {
        try {
          key = `json:${JSON.stringify(candidate)}`;
        } catch {
          key = `obj:${merged.length}`;
        }
      }

      if (!seen.has(key) && merged.length < MAX_LIST_ITEMS) {
        seen.add(key);
        merged.push(candidate);
      }
    };

    primary.forEach(pushValue);
    secondary.forEach(pushValue);
    return merged;
  }

  function isLikelyEmail(value) {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);
  }

  function sanitizeMetadata(metadata, fallback = {}) {
    if (!isPlainObject(metadata)) {
      return isPlainObject(fallback) ? { ...fallback } : {};
    }

    const entries = Object.entries(metadata).slice(0, MAX_METADATA_ENTRIES);
    const sanitized = {};
    for (const [rawKey, rawValue] of entries) {
      if (typeof rawKey !== 'string') continue;
      const trimmedKey = rawKey.trim();
      if (!trimmedKey) continue;
      if (rawValue === undefined || rawValue === null) continue;

      let strValue;
      if (typeof rawValue === 'string') {
        strValue = rawValue.trim();
      } else if (typeof rawValue === 'number' || typeof rawValue === 'boolean') {
        strValue = String(rawValue);
      } else if (Array.isArray(rawValue) || isPlainObject(rawValue)) {
        try {
          strValue = JSON.stringify(rawValue);
        } catch (error) {
          console.warn('Unable to serialize metadata value for key:', trimmedKey, error);
          continue;
        }
      } else {
        strValue = String(rawValue);
      }

      if (!strValue) continue;
      if (strValue.length > MAX_METADATA_VALUE_LENGTH) {
        strValue = strValue.slice(0, MAX_METADATA_VALUE_LENGTH);
      }
      sanitized[trimmedKey] = strValue;
    }

    if (Object.keys(sanitized).length === 0) {
      return isPlainObject(fallback) ? { ...fallback } : {};
    }
    return sanitized;
  }

  function normalizeSharePreferences(preferences = {}, defaults = baseShareOptions) {
    const sharedEmails = coerceStringList(
      preferences.sharedWithEmails,
      defaults.sharedWithEmails,
      isLikelyEmail
    ).map(email => email.toLowerCase());

    const uniqueSharedEmails = Array.from(new Set(sharedEmails)).slice(0, MAX_LIST_ITEMS);

    const normalized = {
      isPublic:
        typeof preferences.isPublic === 'boolean' ? preferences.isPublic : defaults.isPublic === true,
      sharedWithEmails: uniqueSharedEmails,
      plugins: coerceStringList(preferences.plugins, defaults.plugins),
      dataPlatforms: coerceStringList(preferences.dataPlatforms, defaults.dataPlatforms),
      exportTargets: coerceStringList(preferences.exportTargets, defaults.exportTargets),
      metadata: sanitizeMetadata(preferences.metadata, defaults.metadata),
      query:
        typeof preferences.query === 'string' && preferences.query.trim()
          ? preferences.query.trim()
          : typeof defaults.query === 'string' && defaults.query.trim()
            ? defaults.query.trim()
            : null
    };

    return normalized;
  }

  function buildSharePayload(itemId, itemContext, sharePreferences) {
    if (!itemId) {
      console.error('buildSharePayload called without an itemId.');
      return null;
    }

    if (!itemContext || !itemContext.type) {
      console.error('buildSharePayload called without a share context.');
      return null;
    }

    if (!window.userData || !window.userData.id) {
      console.error('buildSharePayload requires userData with an id.');
      return null;
    }

    const normalizedPreferences = normalizeSharePreferences(sharePreferences);

    const payload = {
      v: 1,
      itemId: itemId,
      itemType: itemContext.type,
      chatId: itemContext.chatId || null,
      dashboardId: itemContext.dashboardId || null,
      sharerUserId: window.userData.id,
      ts: Date.now(),
      isPublic: normalizedPreferences.isPublic,
      sharedWithEmails: normalizedPreferences.sharedWithEmails,
      plugins: normalizedPreferences.plugins,
      dataPlatforms: normalizedPreferences.dataPlatforms,
      exportTargets: normalizedPreferences.exportTargets,
      metadata: normalizedPreferences.metadata
    };

    const contextPlugins = coerceStringList(itemContext.plugins, []);
    const contextPlatforms = coerceStringList(
      itemContext.dataPlatforms || itemContext.dataPlatform,
      []
    );
    let contextExports = [];
    if (Array.isArray(itemContext.exportTargets)) {
      const allStrings = itemContext.exportTargets.every(value => typeof value === 'string');
      if (allStrings) {
        contextExports = coerceStringList(itemContext.exportTargets, []);
      }
    } else if (typeof itemContext.exportTargets === 'string') {
      contextExports = coerceStringList(itemContext.exportTargets, []);
    }

    payload.plugins = mergeUniqueLists(normalizedPreferences.plugins, contextPlugins);
    payload.dataPlatforms = mergeUniqueLists(
      normalizedPreferences.dataPlatforms,
      contextPlatforms
    );
    payload.exportTargets = mergeUniqueLists(
      normalizedPreferences.exportTargets,
      contextExports
    );

    if (isPlainObject(itemContext.metadata)) {
      const mergedMetadata = { ...itemContext.metadata, ...payload.metadata };
      payload.metadata = sanitizeMetadata(mergedMetadata, payload.metadata);
    }

    const providedQuery = normalizedPreferences.query;
    const derivedQuery = getQueryForItem(itemId, itemContext);

    if (providedQuery) {
      payload.query = providedQuery;
    } else if (derivedQuery) {
      payload.query = derivedQuery;
    }

    return payload;
  }

  function updateShareDefaults(overrides = {}) {
    baseShareOptions = normalizeSharePreferences(overrides, baseShareOptions);
    return { ...baseShareOptions, metadata: { ...baseShareOptions.metadata } };
  }

  function readRuntimeShareDefaults() {
    if (typeof window === 'undefined' || !window) return null;
    const runtimeConfig =
      window.graphShareConfig && isPlainObject(window.graphShareConfig.defaultShareOptions)
        ? window.graphShareConfig.defaultShareOptions
        : window.graphShareConfig && isPlainObject(window.graphShareConfig)
          ? window.graphShareConfig
          : null;

    if (!runtimeConfig) return null;
    return updateShareDefaults(runtimeConfig);
  }

  sharingSelf.configureShareDefaults = function (overrides = {}) {
    return updateShareDefaults(overrides);
  };

  sharingSelf.getDefaultShareOptions = function () {
    const defaults = normalizeSharePreferences({}, baseShareOptions);
    return {
      ...defaults,
      sharedWithEmails: [...defaults.sharedWithEmails],
      plugins: [...defaults.plugins],
      dataPlatforms: [...defaults.dataPlatforms],
      exportTargets: [...defaults.exportTargets],
      metadata: { ...defaults.metadata }
    };
  };

  sharingSelf.normalizeSharePreferences = function (overrides = {}) {
    const normalized = normalizeSharePreferences(overrides);
    return {
      ...normalized,
      sharedWithEmails: [...normalized.sharedWithEmails],
      plugins: [...normalized.plugins],
      dataPlatforms: [...normalized.dataPlatforms],
      exportTargets: [...normalized.exportTargets],
      metadata: { ...normalized.metadata }
    };
  };

  sharingSelf.buildSharePayload = buildSharePayload;

  sharingSelf.bootstrapRuntimeDefaults = function () {
    return readRuntimeShareDefaults();
  };

  function safeNavigate(targetUrl) {
    if (!targetUrl) return false;
    if (typeof window === 'undefined' || !window || !window.location) return false;
    const userAgent =
      typeof navigator !== 'undefined' && navigator ? String(navigator.userAgent || '') : '';
    if (userAgent.toLowerCase().includes('jsdom')) {
      sharingSelf.lastRedirectAttempt = targetUrl;
      return true;
    }
    sharingSelf.lastRedirectAttempt = targetUrl;
    try {
      if (typeof window.location.assign === 'function') {
        window.location.assign(targetUrl);
      } else {
        window.location.href = targetUrl;
      }
    } catch (error) {
      console.warn('Navigation is not supported in this environment.', error);
    }
    return true;
  }

  function safeRedirectToLogin(sourceUrl, queryParam = 'next') {
    if (typeof window === 'undefined' || !window || !window.location) return false;
    const currentHref =
      typeof sourceUrl === 'string' && sourceUrl ? sourceUrl : window.location.href || '';
    if (!currentHref) return false;
    const encoded = encodeURIComponent(currentHref);
    return safeNavigate(`/login?${queryParam}=${encoded}`);
  }

  sharingSelf.safeRedirectToLogin = safeRedirectToLogin;

  function showShareSnackbar(message, options = {}) {
    if (!message) return false;
    const { timeout = 3000, level = 'info' } = options;
    const container = document.querySelector('#share-snackbar');
    if (
      container &&
      container.MaterialSnackbar &&
      typeof container.MaterialSnackbar.showSnackbar === 'function'
    ) {
      container.MaterialSnackbar.showSnackbar({ message, timeout });
      return true;
    }

    if (typeof showSnackbar === 'function') {
      showSnackbar(message);
      return true;
    }

    if (
      typeof NotificationUtils !== 'undefined' &&
      NotificationUtils &&
      level === 'error' &&
      typeof NotificationUtils.showError === 'function'
    ) {
      NotificationUtils.showError(message);
      return true;
    }

    if (
      typeof NotificationUtils !== 'undefined' &&
      NotificationUtils &&
      level !== 'error' &&
      typeof NotificationUtils.showSuccess === 'function'
    ) {
      NotificationUtils.showSuccess(message);
      return true;
    }

    if (typeof alert === 'function') {
      alert(message);
      return true;
    }

    return false;
  }

  // Helper function to safely access dataAccessor
  function getDataAccessor() {
    return typeof dataAccessor !== 'undefined' && dataAccessor ? dataAccessor : null;
  }

  function notebookStatus(message, isError = false) {
    const status = document.getElementById('notebook-export-status');
    if (!status) return;
    status.textContent = message || '';
    status.classList.toggle('is-error', Boolean(isError));
  }

  function resetNotebookExportUi() {
    notebookStatus('');
    const publicCheckbox = document.getElementById('notebook-export-public');
    if (publicCheckbox) publicCheckbox.checked = false;
    const linkWrap = document.getElementById('notebook-hosted-link');
    const linkInput = document.getElementById('notebook-hosted-link-input');
    if (linkWrap) linkWrap.hidden = true;
    if (linkInput) linkInput.value = '';
  }

  function plotlyPayloadForGraph(graphId) {
    if (!graphId) return null;
    if (window.graphPlotlyCache && typeof window.graphPlotlyCache.get === 'function') {
      const cached = window.graphPlotlyCache.get(graphId);
      if (cached) return cached;
    }
    const target = document.getElementById(`${graphId}graph-target`);
    if (!target || !Array.isArray(target.data) || target.data.length === 0) {
      return null;
    }
    return {
      data: target.data,
      layout: target.layout || {},
      config: target._context || {}
    };
  }

  function chartTitle(plotlyPayload, query) {
    const layoutTitle = plotlyPayload && plotlyPayload.layout ? plotlyPayload.layout.title : null;
    const titleText =
      typeof layoutTitle === 'string'
        ? layoutTitle
        : layoutTitle && typeof layoutTitle.text === 'string'
          ? layoutTitle.text
          : '';
    if (titleText.trim()) return titleText.trim();
    const firstTrace =
      plotlyPayload && Array.isArray(plotlyPayload.data) ? plotlyPayload.data[0] || {} : {};
    const chartType = String(firstTrace.type || 'chart').replace(/_/g, ' ');
    const queryText = String(query || '').trim();
    return queryText ? queryText.slice(0, 100) : `Helix ${chartType} analysis`;
  }

  async function resolveNotebookExportContext(graphIdOverride) {
    const graphIdInput = document.getElementById('share-graph-id');
    const chatIdInput = document.getElementById('share-chat-id');
    const dashboardIdInput = document.getElementById('share-dashboard-id');
    const graphId = graphIdOverride || (graphIdInput ? graphIdInput.value : '');
    const chatId = graphIdOverride ? '' : chatIdInput ? chatIdInput.value : '';
    const dashboardId =
      (dashboardIdInput ? dashboardIdInput.value : '') ||
      (graphIdOverride ? (getDataAccessor() || {}).currentActiveDashboardName || '' : '');
    const plotly = plotlyPayloadForGraph(graphId);
    if (!plotly) {
      throw new Error('This chart has no rendered data to export.');
    }

    let query = '';
    const da = getDataAccessor();
    try {
      if (da && chatId && typeof da.getChatData === 'function') {
        const chatData = await da.getChatData(window.userData, chatId, graphId);
        query = chatData && (chatData.messageName || chatData.query) || '';
      } else if (da && dashboardId && typeof da.getGraphData === 'function') {
        const graphData = await da.getGraphData(window.userData, dashboardId, graphId);
        query = graphData && (graphData.graphName || graphData.query) || '';
      }
    } catch (error) {
      console.warn('Notebook export could not load the stored source request:', error);
    }

    if (!query) {
      const form = Array.from(
        document.querySelectorAll('.askfelixformedit[data-id], .askfelixformeditchat[data-id]')
      ).find(candidate => candidate.getAttribute('data-id') === graphId);
      const input = form ? form.querySelector('.typeahead, input[type="text"], textarea') : null;
      query = input ? input.value || input.getAttribute('value') || '' : '';
    }
    return {
      graphId,
      query,
      plotly,
      title: chartTitle(plotly, query)
    };
  }

  async function withNotebookExportButton(button, callback) {
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Preparing...';
    notebookStatus('Preparing notebook...');
    try {
      await callback();
    } catch (error) {
      const message = error && error.message ? error.message : 'Notebook export failed.';
      notebookStatus(message, true);
      console.error('Notebook export failed:', error);
    } finally {
      button.disabled = false;
      button.textContent = originalText;
    }
  }

  async function hostNotebook(context) {
    const source = buildMarimoNotebook(context);
    const publicCheckbox = document.getElementById('notebook-export-public');
    const visibility = publicCheckbox && publicCheckbox.checked ? 'public' : 'private';
    const secret =
      window.userData && window.userData.secret
        ? window.userData.secret
        : window.localStorage.getItem('secret') || '';
    if (!secret) {
      throw new Error('Sign in before hosting a notebook.');
    }
    const response = await fetch('/api/notebooks', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        secret,
        name: context.title,
        description: context.query
          ? `Generated from the Helix request: ${String(context.query).slice(0, 400)}`
          : 'Generated from a Helix chart.',
        source,
        visibility
      })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload.success || !payload.notebook) {
      throw new Error(payload.error || `Notebook hosting failed with status ${response.status}.`);
    }
    const relativeUrl =
      payload.notebook.url || `/notebooks/${encodeURIComponent(payload.notebook.slug || '')}`;
    const hostedUrl = new URL(relativeUrl, window.location.origin).toString();
    const linkWrap = document.getElementById('notebook-hosted-link');
    const linkInput = document.getElementById('notebook-hosted-link-input');
    const openLink = document.getElementById('notebook-hosted-open-link');
    if (linkInput) linkInput.value = hostedUrl;
    if (openLink) openLink.href = hostedUrl;
    if (linkWrap) linkWrap.hidden = false;
    notebookStatus(
      visibility === 'public'
        ? 'Public notebook hosted. Anyone with this link can view it.'
        : 'Private notebook hosted. This link opens for your signed-in account.'
    );
  }

  async function exportDashboardNotebook() {
    const da = getDataAccessor();
    const title = (da && da.currentActiveDashboardName) || 'Dashboard';
    const graphIds = Array.from(
      new Set(
        Array.from(document.querySelectorAll('.panel-share-button[data-id]'))
          .map(element => element.getAttribute('data-id'))
          .filter(Boolean)
      )
    );
    const contexts = [];
    for (const graphId of graphIds) {
      try {
        contexts.push(await resolveNotebookExportContext(graphId));
      } catch (error) {
        console.warn('Dashboard notebook export skipped a chart:', graphId, error);
      }
    }
    if (contexts.length === 0) {
      throw new Error('This dashboard has no rendered charts to export.');
    }
    const text = await fetchServerDashboardIpynb(title, contexts);
    if (!text) {
      throw new Error('The dashboard notebook could not be generated. Try exporting charts one at a time.');
    }
    downloadNotebookText(
      text,
      notebookFilename(title, 'ipynb'),
      'application/x-ipynb+json;charset=utf-8'
    );
    notebookStatus(
      `Dashboard notebook downloaded (${contexts.length} chart${contexts.length === 1 ? '' : 's'}).`
    );
  }

  function setupNotebookExportButtons(shareDialog) {
    if (!shareDialog || shareDialog.dataset.notebookExportBound === '1') return;
    shareDialog.dataset.notebookExportBound = '1';
    shareDialog.querySelectorAll('[data-notebook-export]').forEach(button => {
      button.addEventListener('click', function () {
        const action = button.getAttribute('data-notebook-export');
        if (action === 'copy-hosted-link') {
          const linkInput = document.getElementById('notebook-hosted-link-input');
          if (linkInput && linkInput.value) {
            copyToClipboard(linkInput, button, button.textContent);
          }
          return;
        }
        withNotebookExportButton(button, async () => {
          if (action === 'ipynb-dashboard') {
            await exportDashboardNotebook();
            return;
          }
          const context = await resolveNotebookExportContext();
          if (action === 'ipynb') {
            downloadNotebookText(
              (await fetchServerIpynb(context)) || buildIpynbNotebook(context),
              notebookFilename(context.title, 'ipynb'),
              'application/x-ipynb+json;charset=utf-8'
            );
            notebookStatus('Jupyter notebook downloaded.');
          } else if (action === 'marimo') {
            downloadNotebookText(
              buildMarimoNotebook(context),
              notebookFilename(context.title, 'py'),
              'text/x-python;charset=utf-8'
            );
            notebookStatus('Marimo notebook downloaded.');
          } else if (action === 'host') {
            await hostNotebook(context);
          }
        });
      });
    });
  }

  // --- START SIMPLE HASH HELPER FUNCTIONS ---
  function simpleBase64Encode(str) {
    if (typeof btoa === 'function') {
      return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } else {
      // Simple base64 polyfill for environments without btoa
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
      let result = '';
      let i = 0;
      while (i < str.length) {
        const a = str.charCodeAt(i++);
        const b = i < str.length ? str.charCodeAt(i++) : 0;
        const c = i < str.length ? str.charCodeAt(i++) : 0;
        const bitmap = (a << 16) | (b << 8) | c;
        result += chars.charAt((bitmap >> 18) & 63);
        result += chars.charAt((bitmap >> 12) & 63);
        result += chars.charAt((bitmap >> 6) & 63);
        result += chars.charAt(bitmap & 63);
      }
      return result.replace(/A+$/, '');
    }
  }

  function simpleBase64Decode(str) {
    if (typeof atob === 'function') {
      const base64 = str.replace(/-/g, '+').replace(/_/g, '/');
      return atob(base64);
    } else {
      // Simple fallback - just return the string for now
      console.warn('Base64 decode not available, returning raw string');
      return str;
    }
  }

  // Simple hash function that works everywhere
  function simpleHash(str) {
    let hash = 0;
    if (str.length === 0) return hash.toString(36);
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }

  function generateSignedShareToken(itemId, itemContext, sharePreferences) {
    // Ensure userData is available before proceeding.
    if (!window.userData || !window.userData.secret || !window.userData.id) {
      console.error(
        'User data (secret or id) is NOT available for token generation. This should not happen if share UI is active.'
      );
      // Optionally, trigger a UI notification or a more robust error handling/retry mechanism.
      alert(
        'Cannot generate share link: User data is not available. Please try refreshing or logging in again.'
      );
      return null;
    }

    const payload = buildSharePayload(itemId, itemContext, sharePreferences);
    if (!payload) {
      console.error('Unable to build share payload for token generation.');
      alert('Cannot generate share link: Missing share metadata.');
      return null;
    }

    const payloadString = JSON.stringify(payload);

    try {
      // Create a simple signature using hash
      const signatureInput = payloadString + '|' + window.userData.secret;
      const signature = simpleHash(signatureInput);

      const payloadB64 = simpleBase64Encode(payloadString);

      if (!payloadB64) {
        console.error('Failed to base64 encode payload.');
        return null;
      }
      return `${payloadB64}.${signature}`;
    } catch (error) {
      console.error('Error signing share token:', error);
      return null;
    }
  }

  // Synchronous full-token share link. Always derivable from the token alone, so
  // callers can populate the share-link cache immediately and (optionally) upgrade
  // to a registered short URL once the async round-trip completes.
  function buildFullTokenShareUrl(token) {
    if (!token) return null;
    return `${window.location.origin}/view_shared?token=${encodeURIComponent(token)}`;
  }

  async function buildShareViewUrl(token) {
    if (!token) return null;
    try {
      const response = await fetch(API_REGISTER_SHARE_TOKEN_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      const data = await response.json();
      if (data && data.success && data.share_url) {
        return data.share_url;
      }
    } catch (error) {
      console.warn('Short share URL registration failed, using full token link:', error);
    }
    return buildFullTokenShareUrl(token);
  }

  // --- END SIMPLE HASH HELPER FUNCTIONS ---

  // Try to retrieve the query text associated with an item
  function getQueryForItem(itemId, itemContext) {
    let selector = null;
    if (itemContext && itemContext.type === 'chat') {
      selector = `.askfelixformeditchat[data-id="${itemId}"] .typeahead`;
    } else {
      selector = `.askfelixformedit[data-id="${itemId}"] .typeahead`;
    }
    const inputEl = document.querySelector(selector);
    if (inputEl && inputEl.value) {
      return inputEl.value;
    }
    return null;
  }
  function verifySignedShareToken(token, secret) {
    if (!token) return false;
    const parts = token.split('.');
    if (parts.length !== 2) return false;
    const [payloadB64, providedSignature] = parts;
    try {
      const payloadString = simpleBase64Decode(payloadB64);
      const signatureInput = payloadString + '|' + secret;
      const expectedSignature = simpleHash(signatureInput);
      return expectedSignature === providedSignature;
    } catch (error) {
      console.error('verifySignedShareToken error:', error);
      return false;
    }
  }

  // Store pre-calculated share links for current items
  sharingSelf.cachedShareLinks = {};
  let precalcHasRunSuccessfully = false; // Flag to track if pre-calculation has run

  // Initialize sharing functionality
  sharingSelf.init = function () {
    sharingSelf.bootstrapRuntimeDefaults();

    // Set up event listeners for share dialog
    const shareDialog = document.getElementById('share-dialog');
    if (!shareDialog) {
      return false;
    }
    setupNotebookExportButtons(shareDialog);

    // Close button
    const closeButtons = shareDialog.querySelectorAll('.close');
    closeButtons.forEach(button => {
      button.addEventListener('click', function () {
        shareDialog.close();
      });
    });

    // Copy link button with improved compatibility
    const copyLinkButton = shareDialog.querySelector('.share-copy-link');
    if (copyLinkButton) {
      // Ensure button exists
      copyLinkButton.addEventListener('click', async function () {
        // Made async
        const linkInput = document.getElementById('share-link');
        if (!linkInput) {
          console.error("Copy Link: 'share-link' input not found.");
          return;
        }

        // Check for user data before proceeding
        if (!window.userData || !window.userData.secret || !window.userData.id) {
          linkInput.value = 'Error: User data not ready. Please wait.';
          console.error('Copy Link: User data not available for token generation.');
          showShareSnackbar('User data not ready. Please try again shortly.', {
            timeout: 3000,
            level: 'error'
          });
          return;
        }

        const originalButtonText = copyLinkButton.textContent;
        copyLinkButton.textContent = 'Generating...';
        copyLinkButton.disabled = true;

        try {
          const graphId = document.getElementById('share-graph-id').value;
          const chatIdValue = document.getElementById('share-chat-id').value; // Renamed to avoid conflict
          const dashboardIdValue = document.getElementById('share-dashboard-id').value; // Renamed
          // const emailString = document.getElementById('share-email') ? document.getElementById('share-email').value : ''; // Removed
          // const isPublic = document.getElementById('share-public-checkbox') ? document.getElementById('share-public-checkbox').checked : true; // Removed, always true

          const itemContext = {};
          if (chatIdValue) {
            itemContext.type = 'chat';
            itemContext.chatId = chatIdValue;
          } else if (dashboardIdValue) {
            itemContext.type = 'dashboard';
            itemContext.dashboardId = dashboardIdValue;
          } else {
            // Fallback to infer context if specific IDs aren't in the hidden fields
            // (This might happen if openShareDialog was called with only itemId)
            const da = getDataAccessor();
            if (da && da.isInChat && da.currentActiveChatName) {
              itemContext.type = 'chat';
              itemContext.chatId = da.currentActiveChatName;
            } else if (da && da.currentActiveDashboardName) {
              itemContext.type = 'dashboard';
              itemContext.dashboardId = da.currentActiveDashboardName;
            }
          }

          if (!graphId || !itemContext.type) {
            console.error('Copy Link: Missing graphId or item context for token generation.');
            linkInput.value = 'Error: Missing item info.';
            copyLinkButton.textContent = originalButtonText;
            copyLinkButton.disabled = false;
            return;
          }

          const currentSharePrefs = sharingSelf.normalizeSharePreferences();

          const token = generateSignedShareToken(graphId, itemContext, currentSharePrefs);

          if (token) {
            const cacheKey = sharingSelf.getShareCacheKey(
              graphId,
              itemContext.chatId,
              itemContext.dashboardId
            );
            linkInput.value = 'Generating share link...';
            buildShareViewUrl(token).then(shareUrl => {
              if (!shareUrl) {
                linkInput.value = 'Error generating link for copy.';
                copyLinkButton.textContent = originalButtonText;
                copyLinkButton.disabled = false;
                return;
              }
              linkInput.value = shareUrl;
              sharingSelf.cachedShareLinks[cacheKey] = shareUrl;
              linkInput.select();
              copyToClipboard(linkInput, copyLinkButton, originalButtonText);
            });
          } else {
            linkInput.value = 'Error generating link for copy.';
            copyLinkButton.textContent = originalButtonText;
            copyLinkButton.disabled = false;
            showShareSnackbar('Error generating link.', { timeout: 3000, level: 'error' });
          }
        } catch (error) {
          console.error('Error during copy link process:', error);
          linkInput.value = 'Error generating link.';
          copyLinkButton.textContent = originalButtonText;
          copyLinkButton.disabled = false;
          showShareSnackbar('Error generating link. Please try again.', {
            timeout: 3000,
            level: 'error'
          });
        }
        // Old logic calling shareGraph is removed.
      });
    } else {
      console.warn("Share dialog: Copy link button '.share-copy-link' not found.");
    }

    // Public checkbox changes should trigger share update - THIS IS NOW REMOVED
    const publicCheckboxElement = document.getElementById('share-public-checkbox');
    if (publicCheckboxElement) {
      // Hide the checkbox and its container as it's no longer used.
      // publicCheckboxElement.style.display = 'none';
      // if (publicCheckboxElement.parentElement && publicCheckboxElement.parentElement.tagName === 'LABEL') {
      //   publicCheckboxElement.parentElement.style.display = 'none'; // Hide label if it's a parent
      // } else {
      //    // Try to find a more generic container if needed
      // }
      // The event listener logic below is no longer needed as the checkbox is gone or non-functional.
    }
    // Public checkbox removed - no warning needed

    // Email field - auto-share when field loses focus
    const emailField = document.getElementById('share-email');
    if (emailField) {
      // The email field is now always hidden, so this listener is not needed.
      // Ensure the container is hidden by default if it wasn't already by toggleEmailInputVisibility
      const emailFieldContainer = document.getElementById('share-email-container');
      if (emailFieldContainer) {
        emailFieldContainer.style.display = 'none';
      }
    }
    // Email field removed - no warning needed

    // Setup share button in header
    const headerShareButton = document.getElementById('share-button');
    if (headerShareButton) {
      headerShareButton.addEventListener('click', function () {
        // Check if we're in a chat or dashboard context
        const da = getDataAccessor();
        if (da && da.isInChat) {
          // Currently in chat, get active chat message
          const activeChatMessageId = sharingSelf.getCurrentActiveChatMessageId();
          if (activeChatMessageId) {
            sharingSelf.openShareDialog(activeChatMessageId, da.currentActiveChatName, null);
          } else {
            // If there's no active message but there are messages, try to get the first one
            const chatContainer = document.querySelector('.chat-container');
            if (chatContainer) {
              const firstMessageIcon = chatContainer.querySelector('.chat-message-icon[data-id]');
              if (firstMessageIcon) {
                const firstMessageId = firstMessageIcon.getAttribute('data-id');
                sharingSelf.openShareDialog(firstMessageId, da.currentActiveChatName, null);
                return;
              }
            }

            // Only show alert if we really can't find any message
            const newMessageWarning =
              'Create a message first to share. Use the chat box at the bottom of the screen.';
            showShareSnackbar(newMessageWarning, { timeout: 4000 });
          }
        } else {
          // Currently in dashboard, get active graph
          const activeGraphId = sharingSelf.getCurrentActiveGraphId();
          if (activeGraphId) {
            sharingSelf.openShareDialog(
              activeGraphId,
              null,
              da ? da.currentActiveDashboardName : null
            );
          } else {
            // Check if there are any graphs at all
            const graphElements = document.querySelectorAll('.grid-stack-item');
            if (graphElements && graphElements.length > 0) {
              // Try to get ID from the first graph we find
              const firstGraph = graphElements[0];
              const formElement = firstGraph.querySelector('.askfelixformedit[data-id]');

              if (formElement) {
                const firstGraphId = formElement.getAttribute('data-id');
                sharingSelf.openShareDialog(
                  firstGraphId,
                  null,
                  da ? da.currentActiveDashboardName : null
                );
                return;
              }
            }

            // Only show alert if we really can't find any graph
            const newGraphWarning =
              'Create a graph first to share. Use the query box at the bottom of the screen.';
            showShareSnackbar(newGraphWarning, { timeout: 4000 });
          }
        }
      });
    }

    sharingSelf.setupShareButtonObserver();

    // Keep the header share button text in sync with the current view
    sharingSelf.updateHeaderShareButtonLabel();
    setInterval(sharingSelf.updateHeaderShareButtonLabel, 2000);

    // Interval will periodically attempt pre-calculation.
    // Initial call will be triggered by attemptFullInitialization when data is ready.
    setInterval(sharingSelf.precalculateShareLinks, 10000);
    console.log('graphSharing.init: Basic listeners and interval set up.');
  };

  // Create a cache key for share links
  sharingSelf.getShareCacheKey = function (graphId, chatId, dashboardId) {
    return `${graphId || ''}|${chatId || ''}|${dashboardId || ''}`;
  };

  // Get the currently active/focused chat message if any
  sharingSelf.getCurrentActiveChatMessageId = function () {
    // Get the most recent chat message in the current chat
    const chatContainer = document.querySelector('.chat-container');
    if (!chatContainer) return null;

    // Find all chat message elements with data-id attribute
    const chatMessageIcons = chatContainer.querySelectorAll('.chat-message-icon[data-id]');
    if (!chatMessageIcons || chatMessageIcons.length === 0) return null;

    // Get the last (most recent) message ID
    const lastMessageIcon = chatMessageIcons[chatMessageIcons.length - 1];
    return lastMessageIcon.getAttribute('data-id');
  };

  // Get the currently active/focused graph if any
  sharingSelf.getCurrentActiveGraphId = function () {
    // Find all graph elements in the dashboard
    const graphElements = document.querySelectorAll('.grid-stack-item');
    if (!graphElements || graphElements.length === 0) return null;

    // Find the most recently added or focused graph
    // For now, we'll use the last one in the DOM
    const lastGraph = graphElements[graphElements.length - 1];
    const formElement = lastGraph.querySelector('.askfelixformedit[data-id]');

    if (formElement) {
      return formElement.getAttribute('data-id');
    }

    return null;
  };

  // Pre-calculate share links for the active content
  sharingSelf.precalculateShareLinks = async function () {
    // Only run if user is logged in and all necessary data is present
    if (!window.userData || !window.userData.secret || !window.userData.id) {
      // console.warn("PrecalculateShareLinks: Skipping, user data not fully available."); // Potentially noisy, can be enabled for debugging
      return;
    }

    if (!precalcHasRunSuccessfully) {
      console.log(
        'PrecalculateShareLinks: User data is now available. Running for the first time or after reset.'
      );
      precalcHasRunSuccessfully = true;
    }
    // console.log("PrecalculateShareLinks: Running with full user data."); // For debugging successful runs

    try {
      const da = getDataAccessor();
      const itemContext = {};
      // Find currently active chat message
      if (da && da.isInChat && da.currentActiveChatName) {
        const messageId = sharingSelf.getCurrentActiveChatMessageId();
        if (messageId) {
          itemContext.type = 'chat';
          itemContext.chatId = da.currentActiveChatName;
          sharingSelf.ensureShareLinkExists(messageId, itemContext);
        }
      }

      // Find currently active dashboard graph
      if (da && !da.isInChat && da.currentActiveDashboardName) {
        const graphId = sharingSelf.getCurrentActiveGraphId();
        if (graphId) {
          itemContext.type = 'dashboard';
          itemContext.dashboardId = da.currentActiveDashboardName;
          sharingSelf.ensureShareLinkExists(graphId, itemContext);
        }
      }

      // Eagerly pre-calculate for all visible graphs/messages
      const chatMessageIcons = document.querySelectorAll('.chat-message-icon[data-id]');
      for (const icon of chatMessageIcons) {
        const messageId = icon.getAttribute('data-id');
        if (messageId && da && da.currentActiveChatName) {
          const chatCtx = { type: 'chat', chatId: da.currentActiveChatName };
          sharingSelf.ensureShareLinkExists(messageId, chatCtx);
        }
      }

      const graphElements = document.querySelectorAll(
        '.grid-stack-item .askfelixformedit[data-id]'
      );
      for (const element of graphElements) {
        const graphId = element.getAttribute('data-id');
        if (graphId && da && da.currentActiveDashboardName) {
          const dashCtx = { type: 'dashboard', dashboardId: da.currentActiveDashboardName };
          sharingSelf.ensureShareLinkExists(graphId, dashCtx);
        }
      }
    } catch (error) {
      console.error('Error pre-calculating share links:', error);
    }
  };

  sharingSelf.isShareReady = function () {
    return !!(window.userData && window.userData.secret);
  };

  // Ensure a share link exists for this item (now generates client-side token)
  sharingSelf.ensureShareLinkExists = function (itemId, itemContext) {
    if (!itemId) return;

    // Key for cache remains the same structure using itemId and context identifiers
    const cacheKey = sharingSelf.getShareCacheKey(
      itemId,
      itemContext.chatId,
      itemContext.dashboardId
    );

    if (sharingSelf.cachedShareLinks[cacheKey]) return; // Already have a link/token

    console.log(`EnsureShareLink: Generating token for ${itemId} (cache key: ${cacheKey})`);
    // Default preferences for pre-calculation (link is public, no specific emails)
    // These might be overridden by user interaction in the dialog later for that specific copied link.
    try {
      const token = generateSignedShareToken(
        itemId,
        itemContext,
        sharingSelf.normalizeSharePreferences()
      );
      if (token) {
        // Warm the cache synchronously with the full-token link so a copy that
        // happens before the async registration resolves still has a usable URL.
        const fullTokenUrl = buildFullTokenShareUrl(token);
        if (fullTokenUrl) {
          sharingSelf.cachedShareLinks[cacheKey] = fullTokenUrl;
        }
        // Then upgrade to a registered short URL when available.
        buildShareViewUrl(token).then(shareUrl => {
          if (shareUrl) {
            sharingSelf.cachedShareLinks[cacheKey] = shareUrl;
            console.log(`EnsureShareLink: Link cached for ${itemId}: ${shareUrl}`);
          }
        });
      } else {
        // Could not generate token (e.g. crypto error, user data missing)
        // We might want to cache a specific error state or leave it empty so it retries.
        // For now, let's leave it empty in cache on failure to allow retry.
        console.warn(`EnsureShareLink: Failed to generate token for ${itemId}`);
      }
    } catch (error) {
      // Catch errors from generateSignedShareToken if it re-throws (e.g. getCryptoKey failure)
      console.error(`EnsureShareLink: Error during token generation for ${itemId}:`, error);
    }
  };

  // Set up click handlers for all share buttons
  sharingSelf.setupShareButtons = function () {
    // Share buttons in chat messages
    document.querySelectorAll('.share-chat-message-icon').forEach(button => {
      button.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        const messageId = this.getAttribute('data-id');

        // First save the chat/graph if needed, this ensures we have something to share
        const da = getDataAccessor();
        if (da && da.saveChatIfNeeded) {
          da.saveChatIfNeeded().then(() => {
            sharingSelf.openShareDialog(messageId, da.currentActiveChatName, null);
          });
        } else {
          sharingSelf.openShareDialog(messageId, da ? da.currentActiveChatName : null, null);
        }
      });
    });

    // Share buttons in dashboard graphs
    document.querySelectorAll('.panel-share-button').forEach(button => {
      button.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        const graphId = this.getAttribute('data-id');

        // First save the dashboard/graph if needed, this ensures we have something to share
        const da = getDataAccessor();
        if (da && da.saveDashboardIfNeeded) {
          da.saveDashboardIfNeeded().then(() => {
            sharingSelf.openShareDialog(graphId, null, da.currentActiveDashboardName);
          });
        } else {
          sharingSelf.openShareDialog(graphId, null, da ? da.currentActiveDashboardName : null);
        }
      });
    });
  };

  // Set up share button observers to ensure all buttons get handlers
  sharingSelf.setupShareButtonObserver = function () {
    // Create a MutationObserver to watch for new content
    const contentObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.addedNodes && mutation.addedNodes.length > 0) {
          // Check if any new nodes have share buttons that need handlers
          mutation.addedNodes.forEach(function (node) {
            // Check if the node is an element
            if (node.nodeType === 1) {
              // Find any new share buttons in this node
              const chatShareButtons = node.querySelectorAll('.share-chat-message-icon');
              const graphShareButtons = node.querySelectorAll('.panel-share-button');

              // If we found new buttons, call setupShareButtons
              if (chatShareButtons.length > 0 || graphShareButtons.length > 0) {
                setTimeout(sharingSelf.setupShareButtons, 100);
              }
            }
          });
        }
      });
    });

    // Start observing the document body for DOM changes
    contentObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Initial setup of share buttons
    sharingSelf.setupShareButtons();
  };

  // Open the share dialog for a specific item
  sharingSelf.openShareDialog = async function (itemId, chatId, dashboardId) {
    const shareDialog = document.getElementById('share-dialog');
    if (!shareDialog) {
      console.error("Share dialog element 'share-dialog' not found!");
      return;
    }

    // Crucial check for userData before opening the dialog or attempting token generation
    if (!window.userData || !window.userData.secret || !window.userData.id) {
      console.error('Cannot open share dialog: User data not available.');
      var snackbarContainer = document.querySelector('#share-snackbar') || document.body; // Fallback for snackbar
      var snackbar = snackbarContainer.MaterialSnackbar || {
        showSnackbar: data => alert(data.message)
      }; // Basic alert fallback
      snackbar.showSnackbar({
        message:
          'Cannot export: User information is not yet loaded. Please wait a moment and try again.',
        timeout: 4000
      });
      return;
    }

    const dialogTitleElement = document.getElementById('share-dialog-title'); // Assuming this is your title element ID

    // Dynamically set the dialog title
    if (dialogTitleElement) {
      if (chatId) {
        dialogTitleElement.textContent = 'Export this Chat';
      } else if (dashboardId && !itemId) {
        // itemId implies a specific graph within a dashboard
        dialogTitleElement.textContent = 'Export this Dashboard';
      } else if (itemId) {
        // Default to item/visualization if it's not a full chat/dashboard link
        dialogTitleElement.textContent = 'Export this Item';
      } else {
        dialogTitleElement.textContent = 'Export';
      }
    } else {
      console.warn("Share dialog title element ('share-dialog-title') not found.");
    }

    const emailInput = document.getElementById('share-email');
    const publicCheckboxElement = document.getElementById('share-public-checkbox');
    const linkInput = document.getElementById('share-link');
    const graphIdInput = document.getElementById('share-graph-id');
    const chatIdInput = document.getElementById('share-chat-id');
    const dashboardIdInput = document.getElementById('share-dashboard-id');

    if (emailInput) {
      emailInput.value = '';
      // Ensure email container is always hidden
      const emailFieldContainer = document.getElementById('share-email-container');
      if (emailFieldContainer) {
        emailFieldContainer.style.display = 'none';
      }
    }
    if (publicCheckboxElement) {
      publicCheckboxElement.checked = true; // Default to public
      publicCheckboxElement.disabled = true; // Disable as it's always public
      // Also hide the checkbox and its label if not done by CSS
      // publicCheckboxElement.style.display = 'none';
      // if (publicCheckboxElement.parentElement && publicCheckboxElement.parentElement.tagName === 'LABEL') {
      //    publicCheckboxElement.parentElement.style.display = 'none';
      // }
    }
    if (linkInput) linkInput.value = 'Generating export link...';

    if (graphIdInput) graphIdInput.value = itemId || '';
    if (chatIdInput) chatIdInput.value = chatId || '';
    if (dashboardIdInput) dashboardIdInput.value = dashboardId || '';
    resetNotebookExportUi();

    const headerShareButton = document.getElementById('share-button');
    if (headerShareButton) {
      headerShareButton.style.display = itemId ? 'block' : 'none';
      sharingSelf.updateHeaderShareButtonLabel();
    }

    shareDialog.showModal();

    // Generate and display initial client-side token link
    if (linkInput && itemId) {
      // Ensure we have itemId to generate a token
      const itemContext = {};
      if (chatId) {
        itemContext.type = 'chat';
        itemContext.chatId = chatId;
      } else if (dashboardId) {
        itemContext.type = 'dashboard';
        itemContext.dashboardId = dashboardId;
      } else {
        // Try to infer context if not explicitly provided (e.g. from current view)
        // This part might need refinement based on how itemId is sourced without chatId/dashboardId
        const da = getDataAccessor();
        if (da && da.isInChat && da.currentActiveChatName) {
          itemContext.type = 'chat';
          itemContext.chatId = da.currentActiveChatName;
        } else if (da && da.currentActiveDashboardName) {
          itemContext.type = 'dashboard';
          itemContext.dashboardId = da.currentActiveDashboardName;
        }
      }

      if (itemContext.type) {
        // Ensure context was determined
        try {
          const token = generateSignedShareToken(
            itemId,
            itemContext,
            sharingSelf.normalizeSharePreferences()
          );
          if (token) {
            linkInput.value = 'Generating export link...';
            buildShareViewUrl(token).then(shareUrl => {
              if (!shareUrl) {
                linkInput.value = 'Error generating link.';
                return;
              }
              linkInput.value = shareUrl;
              highlightShareLink(linkInput);
              const cacheKey = sharingSelf.getShareCacheKey(
                itemId,
                itemContext.chatId,
                itemContext.dashboardId
              );
              sharingSelf.cachedShareLinks[cacheKey] = shareUrl;
            });
          } else {
            linkInput.value = 'Error generating link.';
          }
        } catch (error) {
          console.error('Error generating initial share link in dialog:', error);
          linkInput.value = 'Error generating link.';
        }
      } else {
        console.warn('Could not determine item context for share dialog link generation.');
        linkInput.value = 'Cannot generate link: context unknown.';
      }
    } else if (linkInput && !itemId) {
      linkInput.value = 'Cannot generate link: no item selected.';
      console.warn('openShareDialog called without itemId.');
    }
  };

  // Helper to highlight share link and encourage copying
  function highlightShareLink(inputElement) {
    // Highlight the field with a brief animation
    inputElement.style.transition = 'background-color 0.5s';
    inputElement.style.backgroundColor = '#e8f0fe';

    // Select the text for easy copying
    setTimeout(() => {
      inputElement.select();

      // Remove any existing prompts
      const existingPrompt = inputElement.parentNode.querySelector('.copy-prompt');
      if (existingPrompt) {
        existingPrompt.remove();
      }

      // Reset background after a delay
      setTimeout(() => {
        inputElement.style.backgroundColor = '';
      }, 1500);
    }, 100);
  }

  // Copy the provided link to the clipboard and show feedback
  function copyToClipboard(inputElement, button, originalButtonText) {
    const text = inputElement.value;
    function showCopiedToast() {
      if (!showShareSnackbar('Link copied to clipboard!', { timeout: 3000 })) {
        console.log('Link copied to clipboard!');
      }
    }

    function resetButton() {
      if (button) {
        setTimeout(() => {
          button.textContent = originalButtonText;
          button.disabled = false;
        }, 1500);
      }
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          if (button) button.textContent = 'Copied!';
          showCopiedToast();
          resetButton();
        })
        .catch(() => {
          inputElement.select();
          document.execCommand('copy');
          if (button) button.textContent = 'Copied!';
          showCopiedToast();
          resetButton();
        });
    } else {
      inputElement.select();
      document.execCommand('copy');
      if (button) button.textContent = 'Copied!';
      showCopiedToast();
      resetButton();
    }
  }

  // Update export button label based on current context
  sharingSelf.updateHeaderShareButtonLabel = function () {
    const headerShareButton = document.getElementById('share-button');
    if (!headerShareButton) return;

    let label = 'Export Graph';
    const da = getDataAccessor();
    if (da) {
      if (da.isInChat) {
        label = 'Export Chat';
      } else if (!da.isInChat && da.currentActiveDashboardName) {
        label = 'Export Dashboard';
      }
    }
    headerShareButton.innerHTML = '<i class="material-icons">file_download</i> ' + label;
  };

  // Show a login prompt when user needs to sign in to edit a shared graph
  sharingSelf.showLoginPrompt = function () {
    if (document.getElementById('login-to-edit-container')) return;
    const container = document.createElement('div');
    container.id = 'login-to-edit-container';
    container.style.position = 'fixed';
    container.style.bottom = '20px';
    container.style.right = '20px';
    container.style.backgroundColor = '#fff';
    container.style.border = '1px solid #ccc';
    container.style.padding = '10px';
    container.style.zIndex = '1000';
    const btn = document.createElement('button');
    btn.textContent = 'Sign in to edit this graph';
    btn.className = 'mdl-button mdl-js-button mdl-button--raised';
    btn.addEventListener('click', sharingSelf.showLoginModal);
    container.appendChild(btn);
    document.body.appendChild(container);
  };

  sharingSelf.hideLoginPrompt = function () {
    const container = document.getElementById('login-to-edit-container');
    if (container) container.remove();
  };

  // Inline login modal using FirebaseUI
  sharingSelf.showLoginModal = function () {
    // Instead of showing Firebase UI, redirect to login page
    const currentHref =
      typeof window !== 'undefined' && window && window.location ? window.location.href : '';
    const redirected = sharingSelf.safeRedirectToLogin(currentHref, 'redirect');
    if (!redirected) {
      console.warn('Unable to redirect to login modal; navigation not supported in this environment.');
    }
  };

  // Load a shared graph
  sharingSelf.loadSharedGraph = async function (shareId) {
    /* This function will be deprecated/removed */
    // ... old implementation ...
    // THIS FUNCTION IS NO LONGER THE PRIMARY WAY TO LOAD SHARED GRAPHS
    // IT'S BEING REPLACED BY loadSharedGraphFromToken
    console.warn('sharingSelf.loadSharedGraph is deprecated. Use loadSharedGraphFromToken.');
    // For now, let's make it a no-op or call the new one if possible, though shareId is different from token
    return { success: false, error: 'Deprecated function' };
  };

  // New function to load a shared graph/item using a token
  // Notify listeners that a shared item resolved. This is a side-effect only:
  // a missing CustomEvent constructor or a throwing listener must never bubble up
  // and make a successful resolution look like a failure.
  function dispatchSharedItemEvent(eventName, detail) {
    try {
      const EventCtor =
        (typeof window !== 'undefined' && window.CustomEvent) ||
        (typeof CustomEvent !== 'undefined' ? CustomEvent : null);
      if (!EventCtor) {
        console.warn(`CustomEvent unavailable; skipping ${eventName} dispatch.`);
        return;
      }
      document.dispatchEvent(new EventCtor(eventName, { detail }));
    } catch (error) {
      console.error(`Failed to dispatch ${eventName} event:`, error);
    }
  }

  sharingSelf.loadSharedGraphFromToken = async function (token) {
    if (!token) {
      console.error('loadSharedGraphFromToken called without a token.');
      return { success: false, error: 'No token provided.' };
    }

    try {
      const headers = {
        'Content-Type': 'application/json'
      };
      const viewerSecret =
        (window.userData && window.userData.secret) || localStorage.getItem('secret');
      if (viewerSecret) {
        headers.Authorization = `Bearer ${viewerSecret}`;
      }
      const response = await fetch(API_RESOLVE_SHARE_TOKEN_ENDPOINT, {
        // New endpoint
        method: 'POST',
        headers,
        body: JSON.stringify({ token: token })
      });

      const data = await response.json(); // Server returns { success, graph_id, query, graph_data, is_public, etc. } or { success, error, requires_login }

      if (data.success) {
        if (viewerSecret || data.is_public) {
          console.log('Successfully resolved share token. Graph data:', data);

          if (data.share_slug && window.history && window.history.replaceState) {
            const shortUrl = `${window.location.origin}/view_shared?s=${encodeURIComponent(data.share_slug)}`;
            window.history.replaceState({}, '', shortUrl);
          }

          if (data.item_type === 'chat' && data.chat_data) {
            const sharedChatData = {
              chat_id: data.chat_id,
              chat_name: data.chat_data.chat_name,
              messages: data.chat_data.messages,
              query: data.query,
              is_public: data.is_public
            };
            window.__PRE_RESOLVED_SHARED_CHAT__ = sharedChatData;
            dispatchSharedItemEvent('load-shared-chat', sharedChatData);
          } else if (data.graph_data) {
            const sharedGraphData = {
              graph_id: data.graph_id,
              query: data.query,
              graph_data: data.graph_data,
              is_public: data.is_public,
              item_type: data.item_type,
              chat_id: data.chat_id
            };
            window.__PRE_RESOLVED_SHARED_GRAPH__ = sharedGraphData;
            dispatchSharedItemEvent('load-shared-graph', sharedGraphData);
          } else if (data.query && data.graph_id) {
            console.warn('Share resolved without graph_data; query-only share:', data.query);
          } else {
            console.warn('Token resolved, but graph_data and query missing in server response.');
          }
          return { success: true, graphData: data };
        } else {
          // Not public, and user is not logged in - server should ideally indicate this.
          // Or, if server just denies access without is_public, and we know user isn't logged in.
          console.log('Shared item requires login.');
          sharingSelf.showLoginPrompt();
          return { success: true, requires_login: true };
        }
      } else {
        // Server indicated failure (e.g., token invalid, access denied)
        if (data.requires_login) {
          console.log('Shared item requires login (indicated by server).');
          const currentHref =
            typeof window !== 'undefined' && window && window.location ? window.location.href : '';
          const redirected = sharingSelf.safeRedirectToLogin(currentHref, 'next');
          return {
            success: false,
            redirecting: redirected,
            error: data.error || 'Login required.'
          };
        }
        console.error('Failed to load shared item from token:', data.error);
        return { success: false, error: data.error || 'Failed to load shared item.' };
      }
    } catch (error) {
      console.error('Error in loadSharedGraphFromToken:', error);
      return { success: false, error: error.message };
    }
  };

  // Plot a single stored graph payload into a chat chart target element.
  function plotSharedChatGraph(targetId, graphData) {
    try {
      const gd = typeof graphData === 'string' ? JSON.parse(graphData) : graphData;
      if (!gd) return false;
      const el = document.getElementById(targetId);
      if (!el) return false;
      const traces = Array.isArray(gd.data) ? gd.data : Array.isArray(gd) ? gd : [];
      const layout = gd.layout || {};
      if (window.Plotly && typeof window.Plotly.newPlot === 'function') {
        window.Plotly.newPlot(el, traces, layout, { responsive: true, displaylogo: false });
        const graphId = targetId.replace(/graph-target$/, '');
        if (window.graphPlotlyCache && typeof window.graphPlotlyCache.set === 'function') {
          window.graphPlotlyCache.set(graphId, gd);
        }
        return true;
      }
      el.textContent = 'Shared chart loaded.';
      return true;
    } catch (error) {
      console.error('Error plotting shared chat graph:', error);
      return false;
    }
  }

  function escapeSharedChatText(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function sanitizeSharedChatId(value, fallback) {
    const raw = String(value || fallback || '');
    const safe = raw.replace(/[^A-Za-z0-9_-]/g, '-').replace(/^-+|-+$/g, '');
    return safe || fallback || 'shared-chart';
  }

  // Render a full shared chat (messages + their graphs) into the chat UI.
  sharingSelf.renderSharedChat = function (chatData) {
    if (!chatData || !Array.isArray(chatData.messages)) {
      return false;
    }
    const jq = window.jQuery || window.$;
    if (!jq) {
      console.warn('renderSharedChat: jQuery not available.');
      return false;
    }

    let container = document.querySelector('.chat-container');
    if (!container) {
      const pageContent =
        document.querySelector('.mdl-layout__content') ||
        document.querySelector('.page-content') ||
        document.body;
      container = document.createElement('div');
      container.className = 'chat-container chat-container--open';
      pageContent.appendChild(container);
    } else {
      container.innerHTML = '';
      container.classList.add('chat-container--open');
    }
    const $container = jq(container);

    let rendered = 0;
    let fallbackSeq = 0;
    chatData.messages.forEach(message => {
      const query = (message && message.query) || '';
      const safeQuery = escapeSharedChatText(query);
      const graphs = message && Array.isArray(message.graphs) ? message.graphs : [];

      if (!graphs.length) {
        // Query-only message (no stored graph) - still show the question bubble.
        if (query) {
          $container.append(
            `<div class="chat-log__item chat-log__item--own"><div class="chat-log__message">${safeQuery}</div></div><div class="clearfix"></div>`
          );
        }
        return;
      }

      graphs.forEach(graph => {
        const graphId = sanitizeSharedChatId(
          graph && graph.graph_id,
          'shared-chart-' + fallbackSeq++
        );
        if (typeof window.renderChatMessageTemplate === 'function') {
          $container.append(window.renderChatMessageTemplate(safeQuery, graphId));
        } else {
          $container.append(
            `<div class="chat-log__item chat-log__item--own"><div class="chat-log__message">${safeQuery}</div></div><div class="clearfix"></div>`
          );
        }
        // The message template injects a loading indicator; remove it for static shares.
        $container.find('.loading-chat-bar').remove();

        if (
          typeof window.addNewHTMLToChat === 'function' &&
          typeof window.getChatTemplate === 'function'
        ) {
          window.addNewHTMLToChat(window.getChatTemplate(graphId), graphId);
          if (plotSharedChatGraph(graphId + 'graph-target', graph.graph_data)) {
            rendered++;
          }
        }
      });
    });

    if (typeof window.componentHandler !== 'undefined' && window.componentHandler.upgradeDom) {
      try {
        window.componentHandler.upgradeDom();
      } catch (e) {
        /* non-fatal */
      }
    }

    const headerTitle = document.querySelector('.mdl-layout-title');
    if (headerTitle && chatData.chat_name) {
      headerTitle.textContent = `Shared chat: ${chatData.chat_name}`;
    }
    document.body.classList.add('viewing-shared-chat');
    return rendered > 0;
  };

  // Expose internal helpers for Node-based tests
  if (typeof module !== 'undefined' && module.exports) {
    sharingSelf._test = {
      generateSignedShareToken,
      verifySignedShareToken,
      simpleBase64Encode,
      simpleBase64Decode,
      simpleHash,
      normalizeSharePreferences,
      buildSharePayload,
      updateShareDefaults,
      readRuntimeShareDefaults,
      safeRedirectToLogin,
      showShareSnackbar
    };
  }

  readRuntimeShareDefaults();

  return sharingSelf;
}

// Create the graphSharing object
const graphSharing = createGraphSharing();

// Initialize sharing functionality when document is ready
if (typeof document !== 'undefined') {
  const initializeGraphSharingRuntime = function () {
    const urlParams = new URLSearchParams(window.location.search);
    const sharedToken = urlParams.get('token') || urlParams.get('s');

    // Initialize after user data is loaded using SimpleAuth
    // Use native DOM event listener instead of jQuery
    document.addEventListener('user-ready', function (event) {
      const userData = (event && event.detail) || window.userData;
      if (userData && userData.secret) {
        // User is signed in with SimpleAuth
        console.log('User authenticated with SimpleAuth for sharing.', userData);
        window.userData = userData;
        graphSharing.init(); // Initialize sharing after userData is confirmed

        // Show share button in header for logged in users
        const headerShareButton = document.getElementById('share-button');
        if (headerShareButton) {
          headerShareButton.style.display = 'block';
          // Observer to ensure it stays block if something else changes it
          const observer = new MutationObserver(function (mutations) {
            if (headerShareButton.style.display !== 'block') {
              headerShareButton.style.display = 'block';
            }
          });
          observer.observe(headerShareButton, { attributes: true, attributeFilter: ['style'] });
        }

        graphSharing.hideLoginPrompt();
      } else {
        // User logged out, reset sharing state
        console.log('User logged out. Resetting sharing state.');
        const headerShareButton = document.getElementById('share-button');
        if (headerShareButton) {
          headerShareButton.style.display = 'none';
        }
        // Potentially clear window.userData if it was set
        window.userData = null;
        const loginDialog = document.getElementById('login-dialog');
        if (loginDialog) loginDialog.close();
        if (sharedToken) {
          graphSharing.showLoginPrompt();
        }
      }
    });

    function renderPreResolvedSharedGraph(sharedGraphData) {
      if (!sharedGraphData || !sharedGraphData.graph_data) {
        return false;
      }

      try {
        const graphData =
          typeof sharedGraphData.graph_data === 'string'
            ? JSON.parse(sharedGraphData.graph_data)
            : sharedGraphData.graph_data;

        if (window.loadGraph && typeof window.loadGraph === 'function') {
          window.loadGraph(graphData, sharedGraphData.query);
        } else {
          const shell = document.getElementById('shared-graph-view') || document.createElement('section');
          shell.id = 'shared-graph-view';
          shell.className = 'shared-graph-view';
          shell.setAttribute('data-shared-graph-id', sharedGraphData.graph_id || '');
          shell.textContent = '';

          const header = document.createElement('div');
          header.className = 'shared-graph-view__header';
          const title = document.createElement('h1');
          title.textContent = sharedGraphData.query || 'Shared graph';
          header.appendChild(title);

          const plot = document.createElement('div');
          plot.id = 'shared-graph-plot';
          plot.className = 'shared-graph-view__plot';
          plot.setAttribute('data-shared-graph-rendered', 'false');

          shell.appendChild(header);
          shell.appendChild(plot);

          if (!shell.parentNode) {
            const pageContent = document.querySelector('.page-content') || document.body;
            pageContent.prepend(shell);
          }

          const traces = Array.isArray(graphData.data)
            ? graphData.data
            : Array.isArray(graphData)
              ? graphData
              : [];
          const layout = graphData.layout || {};
          const plotTarget = shell.querySelector('#shared-graph-plot');

          if (plotTarget && window.Plotly && typeof window.Plotly.newPlot === 'function') {
            window.Plotly.newPlot(plotTarget, traces, layout, {
              responsive: true,
              displaylogo: false
            });
            plotTarget.setAttribute('data-shared-graph-rendered', 'true');
          } else if (plotTarget) {
            plotTarget.textContent = 'Shared graph loaded.';
          }
        }

        const headerTitle = document.querySelector('.mdl-layout-title');
        if (headerTitle) {
          headerTitle.textContent = `Shared: ${sharedGraphData.query || 'Graph'}`;
        }
        document.body.classList.add('viewing-shared-graph');
        return true;
      } catch (error) {
        console.error('Error loading pre-resolved shared graph:', error);
        return false;
      }
    }

    // Handle pre-resolved shared graph data (server-side resolution)
    document.addEventListener('load-shared-graph', function (event) {
      const sharedGraphData = event.detail;
      console.log('Loading pre-resolved shared graph:', sharedGraphData);

      // Directly load the graph data without API calls
      if (sharedGraphData.graph_data && !renderPreResolvedSharedGraph(sharedGraphData)) {
        alert('Error loading shared graph data');
      }
    });

    if (window.__PRE_RESOLVED_SHARED_GRAPH__) {
      renderPreResolvedSharedGraph(window.__PRE_RESOLVED_SHARED_GRAPH__);
    }

    // Handle pre-resolved shared chat data (server-side resolution)
    document.addEventListener('load-shared-chat', function (event) {
      const sharedChatData = event.detail;
      console.log('Loading pre-resolved shared chat:', sharedChatData);
      graphSharing.renderSharedChat(sharedChatData);
    });

    if (window.__PRE_RESOLVED_SHARED_CHAT__) {
      graphSharing.renderSharedChat(window.__PRE_RESOLVED_SHARED_CHAT__);
    }

    // Check URL for shared item token. Skip when the server already pre-resolved
    // the item (chat or graph) so we don't double-render or re-fetch.
    if (sharedToken && !window.__PRE_RESOLVED_SHARED_GRAPH__ && !window.__PRE_RESOLVED_SHARED_CHAT__) {
      // Load the shared graph using the new token-based function
      // No need to wait for Firebase auth necessarily, as loadSharedGraphFromToken handles public/private logic
      // and can redirect to login if needed.
      graphSharing.loadSharedGraphFromToken(sharedToken).then(result => {
        if (!result.success && !result.redirecting) {
          // Only alert if not redirecting
          console.error('Error loading shared item from token:', result.error);
          alert(`Error loading shared item: ${result.error || 'Unknown error'}`);
        }
      });
    } // Old `loadSharedGraph(sharedId)` call is removed/replaced by this block.
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeGraphSharingRuntime);
  } else {
    initializeGraphSharingRuntime();
  }
}

// ES module export
export { graphSharing };
